import { useCallback } from "react";
import { useExternalAnalytics } from "@/hooks/useExternalAnalytics";

export type ModuleSlug =
  | "coach"
  | "callback-zone"
  | "castings"
  | "pro"
  | "crew"
  | "hmu"
  | "capture"
  | "draftline"
  | "training-kits";

export type ModuleCTASurface = "catalogue" | "landing" | "header_nav" | "footer" | "hero" | "pricing";
export type ModuleCTAAction = "signup" | "demo" | "sales" | "pricing" | "learn_more" | "secondary" | "primary";

/**
 * Path → module slug map. Returns null when the link is not a module route.
 */
export const moduleFromHref = (href: string): ModuleSlug | null => {
  if (!href) return null;
  if (href.startsWith("/training-kits")) return "training-kits";
  const m = href.match(/^\/(?:platforms|auth|login)\/([a-z-]+)/);
  if (!m) return null;
  const slug = m[1] as ModuleSlug;
  const all: ModuleSlug[] = [
    "coach", "callback-zone", "castings", "pro", "crew", "hmu", "capture", "draftline", "training-kits",
  ];
  return all.includes(slug) ? slug : null;
};

/**
 * Single tracker for every module CTA across catalogue, landing pages, header, footer.
 * Fires GA4 + Meta via useExternalAnalytics.trackBoth.
 */
export const useModuleCTA = () => {
  const { trackBoth } = useExternalAnalytics();

  const trackModuleCTA = useCallback(
    (params: {
      module: ModuleSlug | string;
      surface: ModuleCTASurface;
      action: ModuleCTAAction;
      destination: string;
      label?: string;
    }) => {
      trackBoth("module_cta_click", params);
    },
    [trackBoth]
  );

  return { trackModuleCTA };
};
